import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Form, Alert, ProgressBar } from "react-bootstrap";
import api from "../../services/api";
import { BackgroundJobs } from "../../utils/backgroundJobs";
import { useLanguage } from "../../i18n/LanguageContext";

interface Form106UploadModalProps {
  show: boolean;
  onHide: () => void;
  onComplete: () => void;
}

type UploadPhase = "idle" | "uploading" | "processing" | "done" | "failed";

const POLL_INTERVAL_MS = 3000;

const Form106UploadModal: React.FC<Form106UploadModalProps> = ({
  show,
  onHide,
  onComplete,
}) => {
  const { t, dir } = useLanguage();

  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState("");
  const [phase, setPhase] = useState<UploadPhase>("idle");
  const [jobId, setJobId] = useState<string | null>(null);
  const [progress, setProgress] = useState<{
    processed: number;
    total: number;
  } | null>(null);
  const [result, setResult] = useState<any | null>(null);
  const [errorMsg, setErrorMsg] = useState("");

  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const completedRef = useRef(false);

  useEffect(
    () => () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    if (show) {
      setYear(new Date().getFullYear().toString());
      setFile(null);
      setFileError("");
      setPhase("idle");
      setJobId(null);
      setProgress(null);
      setResult(null);
      setErrorMsg("");
      completedRef.current = false;
    }
  }, [show]);

  useEffect(() => {
    if (!jobId || phase === "done" || phase === "failed") return;
    const poll = async () => {
      try {
        const r = await api.get(`/form106/jobs/${jobId}`);
        const { status, progress: p, result: res, error } = r.data;
        if (p) setProgress(p);
        if (status === "done") {
          setResult(res);
          setPhase("done");
          if (!completedRef.current) {
            completedRef.current = true;
            onComplete();
          }
        } else if (status === "failed") {
          setErrorMsg(error || "Processing failed");
          setPhase("failed");
        } else {
          pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
        }
      } catch {
        pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS * 2);
      }
    };
    pollTimerRef.current = setTimeout(poll, 1500);
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [jobId, phase]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !year) return;
    setPhase("uploading");
    setErrorMsg("");
    try {
      const fd = new FormData();
      fd.append("year", year);
      fd.append("form106", file);
      const r = await api.post("/form106/batch-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setJobId(r.data.jobId);
      setPhase("processing");
    } catch (err: any) {
      setErrorMsg(err.response?.data?.error || "Failed to send file to server");
      setPhase("failed");
    }
  };

  const handleClose = () => {
    if (phase === "uploading") return;
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    // If closing while processing, register as background job so AdminDashboard keeps tracking it
    if (phase === "processing" && jobId) {
      const label = "Form 106" + (year ? ` — ${year}` : "");
      BackgroundJobs.add({ jobId, type: "form106", label });
    }
    onHide();
  };

  const progressPct =
    progress && progress.total > 0
      ? Math.round((progress.processed / progress.total) * 100)
      : null;
  const canSubmit = phase === "idle" && !!file && !!year && !fileError;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      fullscreen="sm-down"
      backdrop={phase === "uploading" ? "static" : true}
      dir={dir}
    >
      <Modal.Header closeButton={phase !== "uploading"}>
        <Modal.Title className="h5">📋 {t("uploadForm106")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {phase === "idle" && (
          <>
            <Alert variant="info" className="small mb-3">
              <strong>📋 {t("form106UploadTitle")}</strong>
              <p className="mb-0 mt-2">{t("form106UploadDesc")}</p>
            </Alert>
            <Form id="form106-upload-form" onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold">{t("year")} *</Form.Label>
                <Form.Control
                  type="number"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  min="2000"
                  max="2100"
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold">
                  {t("multiPagePDFFile")} *
                </Form.Label>
                <Form.Control
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={(e: any) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (f.type !== "application/pdf") {
                      setFileError("Only PDF files are allowed");
                      e.target.value = "";
                      return;
                    }
                    setFileError("");
                    setFile(f);
                  }}
                  required
                />
                {fileError && (
                  <div className="text-danger small mt-1">{fileError}</div>
                )}
                {file && !fileError && (
                  <div className="mt-1">
                    <small className="text-success">
                      ✓ {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                    </small>
                  </div>
                )}
              </Form.Group>
            </Form>
          </>
        )}
        {phase === "uploading" && (
          <div className="text-center py-4">
            <div
              className="spinner-border text-primary mb-3"
              role="status"
              style={{ width: "2.5rem", height: "2.5rem" }}
            >
              <span className="visually-hidden">{t("uploading")}</span>
            </div>
            <h6 className="mb-1">{t("uploadingFile")}</h6>
            <p className="text-muted small mb-0">{t("sendingFile")}</p>
          </div>
        )}
        {phase === "processing" && (
          <div className="py-3">
            <div className="d-flex align-items-center gap-3 mb-4">
              <div
                className="spinner-border text-primary flex-shrink-0"
                role="status"
                style={{ width: "2rem", height: "2rem" }}
              >
                <span className="visually-hidden">{t("loading")}</span>
              </div>
              <div>
                <h6 className="mb-0">{t("processingPages")}</h6>
                <p className="text-muted small mb-0">
                  {progress
                    ? `${t("pageOf")} ${progress.processed} ${t("of")} ${progress.total}`
                    : t("extractingPages")}
                </p>
              </div>
            </div>
            {progressPct !== null ? (
              <ProgressBar
                now={progressPct}
                label={`${progressPct}%`}
                animated
                striped
                variant="primary"
                style={{ height: "20px" }}
              />
            ) : (
              <ProgressBar
                animated
                striped
                now={100}
                variant="info"
                style={{ height: "20px" }}
              />
            )}
            <p className="text-muted small text-center mt-3 mb-0">
              {t("processingBackground")}
            </p>
          </div>
        )}
        {phase === "done" && result && (
          <>
            <Alert
              variant={result.failed > 0 ? "warning" : "success"}
              className="mb-3"
            >
              <strong>
                ✅ {result.successful} {t("successfulPages")}
                {result.failed > 0 &&
                  `, ❌ ${result.failed} ${t("failedPages")}`}{" "}
                {t("outOf")} {result.totalPages} {t("pages")}
              </strong>
            </Alert>
            {result.failed > 0 && (
              <div>
                <h6 className="small fw-bold mb-2">
                  ❌ {t("failedPagesList")}
                </h6>
                <div style={{ maxHeight: "240px", overflowY: "auto" }}>
                  {result.results
                    .filter((r: any) => !r.success)
                    .map((r: any) => (
                      <div
                        key={r.page}
                        className="d-flex gap-2 small py-1 border-bottom"
                      >
                        <span
                          className="text-muted"
                          style={{ minWidth: "60px" }}
                        >
                          {t("pageOf")} {r.page}
                        </span>
                        <span className="text-danger">{r.error}</span>
                        {r.employeeNumber && (
                          <span className="text-muted">
                            (#{r.employeeNumber})
                          </span>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            )}
          </>
        )}
        {phase === "failed" && (
          <Alert variant="danger">
            <strong>{t("uploadFailed")}</strong>
            <p className="mb-0 mt-1 small">{errorMsg}</p>
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        {phase === "idle" && (
          <>
            <Button variant="secondary" onClick={handleClose}>
              {t("cancel")}
            </Button>
            <Button
              variant="primary"
              type="submit"
              form="form106-upload-form"
              disabled={!canSubmit}
            >
              {t("uploadBatch")}
            </Button>
          </>
        )}
        {phase === "uploading" && (
          <Button variant="secondary" disabled>
            {t("uploading")}
          </Button>
        )}
        {phase === "processing" && (
          <Button variant="outline-secondary" onClick={handleClose}>
            {t("closeProcessing")}
          </Button>
        )}
        {(phase === "done" || phase === "failed") && (
          <>
            {phase === "failed" && (
              <Button
                variant="outline-secondary"
                onClick={() => setPhase("idle")}
              >
                {t("tryAgain")}
              </Button>
            )}
            <Button variant="primary" onClick={handleClose}>
              {t("close")}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default Form106UploadModal;
